// An uncaught error, an unhandled promise rejection or a React render crash is sent to POST /ops/client_errors
// (Ops::ClientErrorsController), where it becomes one issue per fingerprint (app/services/ops/CLAUDE.md).
// Error reporting, not tracking: no cookie, no id, no query string. At most MAX_REPORTS per page load.

export const ERROR_REPORT_PATH = '/ops/client_errors'
export const MAX_REPORTS = 3

const MAX_MESSAGE = 300
const MAX_FRAMES = 5
const MAX_FRAME = 200
const MAX_COMPONENT = 100
const MAX_BUILD = 40

export type ErrorKind = 'error' | 'rejection' | 'render'

export interface ErrorReport {
  kind: ErrorKind
  message: string
  frames: string[]
  path: string
  component?: string
  build?: string
}

interface ReporterEnv {
  origin: string
  pathname: () => string
  send: (url: string, body: string) => boolean
  build?: string
}

// "at fn (https://host/a.js?v=1:10:5)" (Chrome) or "fn@https://host/a.js:10:5" (Firefox, Safari).
const FRAME = /((?:https?|[a-z-]+-extension):\/\/[^\s()]+?):(\d+)(?::(\d+))?\)?\s*$/

/** The top stack frames as "file:line:col": our own files as a path, anything else with its origin; never a query. */
export function stackFrames(stack: string | undefined, origin: string): string[] {
  if (!stack) return []
  const frames: string[] = []
  for (const line of stack.split('\n')) {
    const match = FRAME.exec(line.trim())
    if (!match) continue
    const url = match[1].split(/[?#]/)[0]
    const file = url.startsWith(`${origin}/`) ? url.slice(origin.length) : url
    const position = match[3] ? `${match[2]}:${match[3]}` : match[2]
    frames.push(`${file}:${position}`.slice(-MAX_FRAME))
    if (frames.length === MAX_FRAMES) break
  }
  return frames
}

/** The first component in a React component stack ("\n    at PriceBox (…)" or "PriceBox@…"). */
export function componentName(componentStack: string | null | undefined): string | undefined {
  const match = /^\s*(?:at\s+)?([A-Za-z_$][\w$.]*)/m.exec(componentStack?.trim() ?? '')
  return match?.[1].slice(0, MAX_COMPONENT)
}

function describe(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    const message = reason.message ? `${reason.name}: ${reason.message}` : reason.name
    return { message, stack: reason.stack }
  }
  return { message: typeof reason === 'string' ? reason : String(reason) }
}

export function createErrorReporter(env: ReporterEnv) {
  let sent = 0

  function report(
    kind: ErrorKind,
    reason: unknown,
    extra: { message?: string; component?: string; stack?: string } = {},
  ): boolean {
    if (sent >= MAX_REPORTS) return false
    const described = describe(reason)
    const message = described.message
    const stack = described.stack || extra.stack
    const text = (extra.message ?? message).trim().slice(0, MAX_MESSAGE)
    if (!text) return false

    const body: ErrorReport = { kind, message: text, frames: stackFrames(stack, env.origin), path: env.pathname() }
    if (extra.component) body.component = extra.component.slice(0, MAX_COMPONENT)
    if (env.build) body.build = env.build.slice(0, MAX_BUILD)

    sent += 1
    try {
      return env.send(ERROR_REPORT_PATH, JSON.stringify(body))
    } catch {
      return false
    }
  }

  function install(target: Window) {
    target.addEventListener('error', (event: ErrorEvent) => {
      // A failed <img> or <script> load also fires "error" (not an ErrorEvent, no message): not a JS error.
      if (!event.message && !event.error) return
      const message = event.error instanceof Error ? undefined : event.message
      // Without an Error object (e.g. another origin's script), the event's own file and line are the only frame.
      const stack = event.filename ? `at (${event.filename}:${event.lineno}:${event.colno})` : undefined
      report('error', event.error ?? event.message, { message, stack })
    })
    target.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      report('rejection', event.reason)
    })
  }

  return { report, install }
}

/** The bundle's content hash from its own URL (e.g. inertia-AbC123xy.js → AbC123xy); "dev" under the Vite dev server. */
export function buildHash(moduleUrl: string): string {
  return /-([A-Za-z0-9_-]{8,})\.js(?:$|\?)/.exec(moduleUrl)?.[1] ?? 'dev'
}

function browserReporter() {
  return createErrorReporter({
    origin: window.location.origin,
    pathname: () => window.location.pathname,
    // text/plain keeps the beacon a simple request; the server reads the raw JSON.
    send: (url, body) => navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' })),
    build: buildHash(import.meta.url),
  })
}

let shared: ReturnType<typeof createErrorReporter> | null = null

/** The page's one reporter (created on first use, in the browser only). */
export function errorReporter() {
  shared ??= browserReporter()
  return shared
}

type Reporter = ReturnType<typeof createErrorReporter>

/**
 * React 19 root options: a render crash is reported as 'render' with the component that threw, and still logged to
 * the console as React does without the option.
 */
export function reactRootErrorOptions(reporter: Reporter) {
  return {
    onUncaughtError(error: unknown, errorInfo: { componentStack?: string | null }) {
      reporter.report('render', error, { component: componentName(errorInfo.componentStack) })
      console.error(error)
    },
  }
}
